import { useEffect, useRef, useState } from 'react';
import { auth, db } from '../../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { MODES } from './constants';
import { todayLocal, formatDateForInput } from './utils';
import { getCurrentSeason, SEASONS } from '../../data/seasons';
import { listSeasons } from '../../utils/seasonApi';
import {
  getCachedPlayerSeasonEntries,
  loadPlayerSeasonEntriesOnce,
} from '../../utils/playerStatsSessionCache';
import { getSeasonConfig } from '../../utils/matchTypeCatalog';
import { buildModeInsights } from './playerDashboardInsights';

const DEBUG_PLAYER_DASHBOARD = false;
const PLAYER_DASHBOARD_DIAGNOSTICS = process.env.NODE_ENV !== 'production';
const CACHE_TTL_MS = 10 * 60 * 1000;
const playerEntriesSessionCache = new Map();

const debugLog = (...args) => {
  if (DEBUG_PLAYER_DASHBOARD) {
    console.debug(...args);
  }
};

const normalizeText = (value) => String(value || '').trim().toUpperCase();
const normalizeResult = (value) => {
  const raw = normalizeText(value);
  if (raw === 'WIN' || raw === 'W') return 'Win';
  if (raw === 'LOSE' || raw === 'LOSS' || raw === 'L') return 'Loss';
  return '';
};

const applyClientFilters = (rows, { startDate, endDate, matchType, region }) =>
  rows.filter((row) => {
    if (startDate && row.Date && row.Date < startDate) return false;
    if (endDate && row.Date && row.Date > endDate) return false;
    if (matchType && normalizeText(row.MatchType) !== normalizeText(matchType)) return false;
    if (region && normalizeText(row.Region) !== normalizeText(region)) return false;
    return true;
  });

const getFilterBreakdown = (rows = [], { startDate, endDate, matchType, region }, mapFilters = null) => {
  const afterDateRows = rows.filter((row) => {
    if (startDate && row.Date && row.Date < startDate) return false;
    if (endDate && row.Date && row.Date > endDate) return false;
    return true;
  });
  const afterMatchTypeRows = afterDateRows.filter((row) => {
    if (matchType && normalizeText(row.MatchType) !== normalizeText(matchType)) return false;
    return true;
  });
  const afterRegionRows = afterMatchTypeRows.filter((row) => {
    if (region && normalizeText(row.Region) !== normalizeText(region)) return false;
    return true;
  });
  const afterMapRows = mapFilters
    ? afterRegionRows.filter((row) => {
        const mode = normalizeModeValue(row.Mode);
        return !(mode && mapFilters[mode] && row.Map !== mapFilters[mode]);
      })
    : afterRegionRows;

  return {
    rawRowCount: rows.length,
    afterDate: afterDateRows.length,
    afterMatchType: afterMatchTypeRows.length,
    afterRegion: afterRegionRows.length,
    afterMap: afterMapRows.length,
  };
};

const getDistinctValues = (rows = [], key) =>
  [...new Set(rows.map((row) => row?.[key]).filter((value) => value !== undefined && value !== null && value !== ''))]
    .map((value) => String(value))
    .sort();

const getDateRepresentations = (rows = []) =>
  [...new Set(rows.map((row) => {
    const value = row?.Date;
    if (!value) return 'empty';
    if (typeof value?.toDate === 'function') return 'firestore-timestamp';
    if (value instanceof Date) return 'date-object';
    if (typeof value === 'string') return `string:${value}`;
    return typeof value;
  }))].slice(0, 20);

const summarizeRowsForDiagnostic = (rows = []) => ({
  values: {
    MatchType: getDistinctValues(rows, 'MatchType'),
    Region: getDistinctValues(rows, 'Region'),
    MatchCategory: getDistinctValues(rows, 'MatchCategory'),
    MatchTypeRegion: getDistinctValues(rows, 'MatchTypeRegion'),
    Mode: getDistinctValues(rows, 'Mode'),
    DateRepresentation: getDateRepresentations(rows),
  },
});

const createLoadDiagnostic = ({
  uid,
  teamId,
  selectedSeason,
  seasonDocId,
  startDate,
  endDate,
  matchType,
  region,
  mapFilters,
  seasons,
}) => {
  if (!PLAYER_DASHBOARD_DIAGNOSTICS) return null;
  const matchedSeason =
    (seasons || []).find((s) => String(s.year || s.id) === String(selectedSeason)) ||
    (seasons || []).find((s) => String(s.id) === String(selectedSeason)) ||
    null;

  return {
    uid,
    teamId,
    selectedSeason,
    seasonDocId,
    seasonDocIdFlag: seasonDocId && selectedSeason && String(seasonDocId) !== String(selectedSeason)
      ? `seasonDocId is not literal selectedSeason (${seasonDocId} !== ${selectedSeason})`
      : '',
    listSeasonsResult: seasons || [],
    matchedSeason,
    filters: { startDate, endDate, matchType, region, mapFilters },
    sources: [],
    selectedDataSource: '',
    rawRowCount: 0,
    normalizedRowCount: 0,
    filteredRowCount: 0,
    filterBreakdown: { rawRowCount: 0, afterDate: 0, afterMatchType: 0, afterRegion: 0, afterMap: 0 },
    maintenanceAssignments: [],
    maintenanceMode: false,
    statsMaintenance: false,
    finalCounts: { HP: 0, SND: 0, CTL: 0 },
    values: {
      MatchType: [],
      Region: [],
      MatchCategory: [],
      MatchTypeRegion: [],
      Mode: [],
      DateRepresentation: [],
    },
    errors: [],
  };
};

const recordSourceAttempt = (diagnostic, attempt) => {
  if (!diagnostic) return;
  diagnostic.sources.push(attempt);
  if (attempt.values && (attempt.normalizedRowCount || 0) > 0) {
    diagnostic.values = attempt.values;
  }
  if (attempt.status === 'FAILED') {
    diagnostic.errors.push({
      source: attempt.source,
      code: attempt.errorCode || '',
      message: attempt.errorMessage || '',
    });
  }
};

const recordRowsDiagnostic = (diagnostic, rows, filters, source) => {
  if (!diagnostic || !Array.isArray(rows)) return;
  diagnostic.selectedDataSource = source || diagnostic.selectedDataSource;
  diagnostic.rawRowCount = rows.length;
  diagnostic.normalizedRowCount = rows.length;
  diagnostic.filterBreakdown = getFilterBreakdown(rows, filters);
  diagnostic.filteredRowCount = diagnostic.filterBreakdown.afterRegion;
  diagnostic.values = {
    MatchType: getDistinctValues(rows, 'MatchType'),
    Region: getDistinctValues(rows, 'Region'),
    MatchCategory: getDistinctValues(rows, 'MatchCategory'),
    MatchTypeRegion: getDistinctValues(rows, 'MatchTypeRegion'),
    Mode: getDistinctValues(rows, 'Mode'),
    DateRepresentation: getDateRepresentations(rows),
  };
};

const selectDiagnosticSource = (diagnostic, sourceName) => {
  if (!diagnostic || !sourceName) return;
  const attempt = [...diagnostic.sources].reverse().find((item) => item.source === sourceName);
  diagnostic.selectedDataSource = sourceName;
  if (!attempt) return;
  diagnostic.rawDocumentCount = attempt.documentCount ?? 0;
  diagnostic.rawRowCount = attempt.normalizedRowCount ?? 0;
  diagnostic.normalizedRowCount = attempt.normalizedRowCount ?? 0;
  diagnostic.filteredRowCount = attempt.filteredRowCount ?? attempt.normalizedRowCount ?? 0;
  if (attempt.filterBreakdown) diagnostic.filterBreakdown = attempt.filterBreakdown;
  if (attempt.values) diagnostic.values = attempt.values;
};

const flushLoadDiagnostic = (diagnostic) => {
  if (!diagnostic || !PLAYER_DASHBOARD_DIAGNOSTICS) return;

  console.group('[PlayerDashboard] Load Diagnostic');
  console.log('uid:', diagnostic.uid);
  console.log('teamId:', diagnostic.teamId);
  console.log('selectedSeason:', diagnostic.selectedSeason);
  console.log('resolved seasonDocId:', diagnostic.seasonDocId);
  console.log('listSeasons result:', diagnostic.listSeasonsResult);
  console.log('matched season object:', diagnostic.matchedSeason);
  if (diagnostic.seasonDocIdFlag) console.warn(diagnostic.seasonDocIdFlag);
  console.log('filters:', diagnostic.filters);
  diagnostic.sources.forEach((source) => {
    console.log('[PlayerDashboard][SOURCE]', source);
  });
  console.log('selected data source:', diagnostic.selectedDataSource || 'none');
  console.log('raw document count:', diagnostic.rawDocumentCount ?? diagnostic.rawRowCount);
  console.log('normalized row count:', diagnostic.normalizedRowCount);
  console.log('filtered row count:', diagnostic.filteredRowCount);
  console.log('filter stages:', diagnostic.filterBreakdown);
  console.log('maintenance assignments:', diagnostic.maintenanceAssignments);
  console.log('maintenanceMode:', diagnostic.maintenanceMode);
  console.log('statsMaintenance:', diagnostic.statsMaintenance);
  console.log('final HP row count:', diagnostic.finalCounts.HP);
  console.log('final SND row count:', diagnostic.finalCounts.SND);
  console.log('final CTL row count:', diagnostic.finalCounts.CTL);
  console.log('MatchType values:', diagnostic.values.MatchType);
  console.log('Region values:', diagnostic.values.Region);
  console.log('MatchCategory values:', diagnostic.values.MatchCategory);
  console.log('MatchTypeRegion values:', diagnostic.values.MatchTypeRegion);
  console.log('Mode values:', diagnostic.values.Mode);
  console.log('Date representation/type:', diagnostic.values.DateRepresentation);
  console.log('errors encountered:', diagnostic.errors);
  console.groupEnd();
};

const normalizeModeValue = (value) => {
  const raw = String(value || '').trim().toUpperCase();
  if (!raw) return '';
  if (raw === 'HP' || raw === 'HARDPOINT') return 'HP';
  if (raw === 'SND' || raw === 'SEARCH' || raw === 'SEARCHANDDESTROY' || raw === 'SEARCH & DESTROY') return 'SND';
  if (raw === 'CTL' || raw === 'CONTROL') return 'CTL';
  return raw;
};

const inferModeFromIds = ({ modeId, matchId }) => {
  const source = String(modeId || matchId || '').toLowerCase();
  if (source.includes('mode_hp_') || source.includes('hp')) return 'HP';
  if (source.includes('mode_snd_') || source.includes('snd')) return 'SND';
  if (source.includes('mode_ctl_') || source.includes('ctl') || source.includes('control')) return 'CTL';
  return '';
};

const mergeUniqueRows = (primaryRows = [], extraRows = []) => {
  const seen = new Set();
  const out = [];
  [...primaryRows, ...extraRows].forEach((row) => {
    const key = row?._rowId ||
      row?.id ||
      `${row?.ModeID || ''}|${row?.Player || ''}|${row?.Date || ''}|${row?.Map || ''}|${row?.Score || ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });
  return out;
};

const getUniqueMatchCount = (rows = []) =>
  new Set(
    rows
      .map((row) => row?.matchId || row?.ModeID || row?.id)
      .filter(Boolean)
  ).size;

const normalizeEntryRow = (data, { id, matchId, matchMeta } = {}) => {
  const entry = data || {};
  const meta = matchMeta || {};
  const modeId = entry.ModeID || meta.ModeID || '';
  const normalizedMode =
    normalizeModeValue(entry.Mode || meta.Mode) ||
    inferModeFromIds({ modeId, matchId });

  return {
    ...(meta || {}),
    ...(entry || {}),
    id: id || entry.id || '',
    ...(matchId ? { matchId } : {}),
    Mode: normalizedMode,
    ModeID: modeId,
    Date: entry.Date || meta.Date || '',
    Map: entry.Map || meta.Map || '',
    MatchType: normalizeText(entry.MatchType || meta.MatchType || ''),
    Region: normalizeText(entry.Region || meta.Region || ''),
    VS: entry.VS || meta.VS || '',
    Result: normalizeResult(entry.Result || entry.WinLose || meta.Result || meta.WinLose),
    PF: Number(entry.PF ?? entry.pf ?? meta.PF ?? meta.pf ?? 0),
    PA: Number(entry.PA ?? entry.pa ?? meta.PA ?? meta.pa ?? 0),
  };
};

const buildCacheKey = ({ teamId, seasonDocId, selectedSeason, playerId, startDate, endDate, matchType, region }) =>
  [
    teamId || '',
    seasonDocId || 'root',
    selectedSeason || '',
    playerId || 'all',
    startDate || '',
    endDate || '',
    matchType || '',
    region || '',
  ].join(':');

const getCachedRows = (key) => {
  const cached = playerEntriesSessionCache.get(key);
  if (!cached) return null;
  if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
    playerEntriesSessionCache.delete(key);
    return null;
  }
  return cached.rows;
};

const setCachedRows = (key, rows) => {
  playerEntriesSessionCache.set(key, {
    timestamp: Date.now(),
    rows,
  });
};

const fetchFlatEntriesAtPath = async ({ teamId, seasonDocId, diagnostic, sourceName = 'season-flat' }) => {
  const path =
    `teams/${teamId}/seasons/${seasonDocId}/database/playerStats/entries`;

  try {
    const rows = await loadPlayerSeasonEntriesOnce(teamId, seasonDocId, async () => {
      const entriesRef = collection(db, path);
      const snapshot = await getDocs(query(entriesRef));
      return snapshot.docs.map((d) => normalizeEntryRow(d.data(), { id: d.id }));
    });

    recordSourceAttempt(diagnostic, {
      source: sourceName,
      path,
      status: 'SUCCESS',
      documentCount: rows.length,
      normalizedRowCount: rows.length,
      ...summarizeRowsForDiagnostic(rows),
    });

    return {
      rows,
      path,
      count: rows.length,
    };
  } catch (err) {
    recordSourceAttempt(diagnostic, {
      source: sourceName,
      path,
      status: 'FAILED',
      documentCount: 0,
      errorCode: err?.code || '',
      errorMessage: err?.message || String(err),
    });
    throw err;
  }
};

const fetchNestedMatchEntries = async ({ matchesPath, filters, diagnostic, sourceName = 'nested-matches' }) => {
  try {
    const matchesRef = collection(db, matchesPath);
    const matchSnap = await getDocs(query(matchesRef));

    const rowBuckets = await Promise.all(
      matchSnap.docs.map(async (matchDoc) => {
        const matchMeta = matchDoc.data() || {};
        const entriesSnap = await getDocs(collection(matchDoc.ref, 'entries'));

        return entriesSnap.docs.map((entryDoc) =>
          normalizeEntryRow(entryDoc.data(), {
            id: `${matchDoc.id}__${entryDoc.id}`,
            matchId: matchDoc.id,
            matchMeta,
          })
        );
      })
    );

    const rows = rowBuckets.flat();
    const filteredRows = applyClientFilters(rows, filters);
    recordSourceAttempt(diagnostic, {
      source: sourceName,
      path: matchesPath,
      status: 'SUCCESS',
      documentCount: matchSnap.size,
      normalizedRowCount: rows.length,
      filteredRowCount: filteredRows.length,
      filterBreakdown: getFilterBreakdown(rows, filters),
      ...summarizeRowsForDiagnostic(rows),
    });
    return filteredRows;
  } catch (err) {
    recordSourceAttempt(diagnostic, {
      source: sourceName,
      path: matchesPath,
      status: 'FAILED',
      documentCount: 0,
      errorCode: err?.code || '',
      errorMessage: err?.message || String(err),
    });
    throw err;
  }
};

const fetchPreferredFlatEntries = async ({ teamId, seasonDocId, filters, diagnostic }) => {
  const path = `teams/${teamId}/seasons/${seasonDocId}/database/playerStats/entries`;
  const cachedRows = getCachedPlayerSeasonEntries(teamId, seasonDocId);
  if (Array.isArray(cachedRows)) {
    const filteredRows = applyClientFilters(cachedRows, filters);
    recordSourceAttempt(diagnostic, {
      source: 'season-flat-session-cache',
      path,
      status: 'SUCCESS',
      documentCount: cachedRows.length,
      normalizedRowCount: cachedRows.length,
      filteredRowCount: filteredRows.length,
      filterBreakdown: getFilterBreakdown(cachedRows, filters),
      ...summarizeRowsForDiagnostic(cachedRows),
    });
    return {
      entries: filteredRows,
      source: 'flatSeasonEntriesCache',
      matchCount: getUniqueMatchCount(filteredRows),
    };
  }

  try {
    const result = await fetchFlatEntriesAtPath({ teamId, seasonDocId, diagnostic, sourceName: 'season-flat' });
    const filteredRows = applyClientFilters(result.rows, filters);
    if (diagnostic?.sources.length) {
      const lastSource = diagnostic.sources[diagnostic.sources.length - 1];
      if (lastSource.source === 'season-flat' && lastSource.path === result.path) {
        lastSource.filteredRowCount = filteredRows.length;
        lastSource.filterBreakdown = getFilterBreakdown(result.rows, filters);
      }
    }
    debugLog('player entries fetch source=flatSeasonEntries', {
      path: result.path,
      count: filteredRows.length,
      filtered: false,
    });
    return {
      entries: filteredRows,
      source: 'flatSeasonEntries',
      matchCount: getUniqueMatchCount(filteredRows),
      hasData: result.rows.length > 0,
    };
  } catch (err) {
    debugLog('player entries flat query failed', { error: err?.message || err });
  }

  return { entries: [], source: 'flatSeasonEntries', matchCount: 0, hasData: false };
};

const fetchLegacySeasonEntries = async ({ teamId, seasonDocId, filters, diagnostic }) => {
  return fetchNestedMatchEntries({
    matchesPath: `teams/${teamId}/seasons/${seasonDocId}/database/playerStats/matches`,
    filters,
    diagnostic,
    sourceName: 'season-nested-matches',
  });
};

const fetchRootFlatEntries = async ({ teamId, filters, diagnostic }) => {
  const path = `teams/${teamId}/database/playerStats/entries`;
  try {
    const entriesSnap = await getDocs(collection(db, path));
    const rows = entriesSnap.docs.map((d) => normalizeEntryRow(d.data(), { id: d.id }));
    const filteredRows = applyClientFilters(rows, filters);
    recordSourceAttempt(diagnostic, {
      source: 'root-flat',
      path,
      status: 'SUCCESS',
      documentCount: entriesSnap.size,
      normalizedRowCount: rows.length,
      filteredRowCount: filteredRows.length,
      filterBreakdown: getFilterBreakdown(rows, filters),
      ...summarizeRowsForDiagnostic(rows),
    });
    return filteredRows;
  } catch (err) {
    recordSourceAttempt(diagnostic, {
      source: 'root-flat',
      path,
      status: 'FAILED',
      documentCount: 0,
      errorCode: err?.code || '',
      errorMessage: err?.message || String(err),
    });
    throw err;
  }
};

const fetchRootMatches = async ({ teamId, filters, diagnostic }) => {
  return fetchNestedMatchEntries({
    matchesPath: `teams/${teamId}/database/playerStats/matches`,
    filters,
    diagnostic,
    sourceName: 'root-nested-matches',
  });
};

const fetchLegacyRootPlayerStats = async ({ teamId, filters, diagnostic }) => {
  const path = `teams/${teamId}/database/teamStats/playerstats`;
  try {
    const legacyCol = collection(db, path);
    const legacySnap = await getDocs(legacyCol);
    const rows = legacySnap.docs.map((d) => normalizeEntryRow(d.data(), { id: d.id }));
    const filteredRows = applyClientFilters(rows, filters);
    recordSourceAttempt(diagnostic, {
      source: 'legacy-root-teamstats-playerstats',
      path,
      status: 'SUCCESS',
      documentCount: legacySnap.size,
      normalizedRowCount: rows.length,
      filteredRowCount: filteredRows.length,
      filterBreakdown: getFilterBreakdown(rows, filters),
      ...summarizeRowsForDiagnostic(rows),
    });
    return filteredRows;
  } catch (err) {
    recordSourceAttempt(diagnostic, {
      source: 'legacy-root-teamstats-playerstats',
      path,
      status: 'FAILED',
      documentCount: 0,
      errorCode: err?.code || '',
      errorMessage: err?.message || String(err),
    });
    throw err;
  }
};

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toHillSeconds = (value) => {
  if (typeof value === 'number') return toNumber(value);
  if (typeof value === 'string' && value.includes(':')) {
    const parts = value.split(':').map(Number);
    if (parts.length === 2 && parts.every((n) => Number.isFinite(n))) {
      return (parts[0] * 60) + parts[1];
    }
  }
  return toNumber(value);
};

export const usePlayerDashboard = () => {
  const [uid, setUid] = useState(null);
  const [teamId, setTeamId] = useState('');
  const [bootstrapLoaded, setBootstrapLoaded] = useState(false);
  const [cachedUserDoc, setCachedUserDoc] = useState(null);
  const [cachedStatsConfig, setCachedStatsConfig] = useState(null);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState(() => todayLocal());
  const [matchType, setMatchType] = useState('');
  const [region, setRegion] = useState('');
  const [matchTypes, setMatchTypes] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [mapFilters, setMapFilters] = useState({ HP: '', SND: '', CTL: '' });
  const [data, setData] = useState({ HP: [], SND: [], CTL: [] });
  const [modeInsights, setModeInsights] = useState({ HP: [], SND: [], CTL: [] });
  const [hpRankings, setHpRankings] = useState(null);
  const [sndRankings, setSndRankings] = useState(null);
  const [ctlRankings, setCtlRankings] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(getCurrentSeason());
  const [seasonDocId, setSeasonDocId] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [activePlayers, setActivePlayers] = useState([]);
  const [playerStats, setPlayerStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [summaryStats, setSummaryStats] = useState(null);
  const [totalGamesCount, setTotalGamesCount] = useState(0);
  const [statsMaintenance, setStatsMaintenance] = useState(false);
  const [playerSearch, setPlayerSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [comparePlayers, setComparePlayers] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [playerImages, setPlayerImages] = useState({});

  const fetchCounterRef = useRef(0);
  const pendingDiagnosticRef = useRef(null);
  const latestMapFiltersRef = useRef(mapFilters);

  useEffect(() => {
    latestMapFiltersRef.current = mapFilters;
  }, [mapFilters]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid || null);

      if (!user) {
        setTeamId('');
        setCachedUserDoc(null);
        setCachedStatsConfig(null);
        setSeasons([]);
        setSeasonDocId(null);
        setPlayerStats([]);
        setBootstrapLoaded(false);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!uid) return;

    if (cachedUserDoc && cachedUserDoc.__uid === uid) {
      setTeamId(cachedUserDoc.teamId || '');
      return;
    }

    let active = true;

    (async () => {
      debugLog('bootstrap read users', { uid });
      const userRef = doc(db, `users/${uid}`);
      const userSnap = await getDoc(userRef);
      if (!active) return;

      if (!userSnap.exists()) {
        setCachedUserDoc(null);
        setTeamId('');
        setBootstrapLoaded(true);
        return;
      }

      const userData = userSnap.data() || {};
      setCachedUserDoc({ ...userData, __uid: uid });
      setTeamId(userData.teamId || '');
    })().catch((err) => {
      console.error('Failed to resolve player dashboard user', err);
      if (!active) return;
      setTeamId('');
      setBootstrapLoaded(true);
    });

    return () => {
      active = false;
    };
  }, [uid, cachedUserDoc]);

  useEffect(() => {
    if (!uid || !teamId) return;

    let active = true;
    setBootstrapLoaded(false);

    (async () => {
      debugLog('bootstrap read config', { teamId });
      const configRef = doc(db, `teams/${teamId}/configurations/stats`);
      const configSnap = await getDoc(configRef);
      if (!active) return;
      setCachedStatsConfig(configSnap.exists() ? configSnap.data() : null);

      debugLog('bootstrap listSeasons', { teamId });
      const rows = await listSeasons(teamId);
      if (!active) return;
      setSeasons(rows);
      setBootstrapLoaded(true);
    })().catch((err) => {
      console.error('Failed to bootstrap player dashboard', err);
      if (!active) return;
      setCachedStatsConfig(null);
      setSeasons([]);
      setBootstrapLoaded(true);
    });

    return () => {
      active = false;
    };
  }, [uid, teamId]);

  useEffect(() => {
    const row =
      seasons.find((s) => String(s.year || s.id) === String(selectedSeason)) ||
      seasons.find((s) => String(s.id) === String(selectedSeason)) ||
      seasons[0] ||
      null;

    // If a season is selected but not present in seasons metadata, prefer selectedSeason
    // as doc id so data under /seasons/{year}/... can still be read.
    if (row) {
      setSeasonDocId(String(row.id));
      return;
    }
    setSeasonDocId(selectedSeason ? String(selectedSeason) : null);
  }, [selectedSeason, seasons]);

  useEffect(() => {
    const seasonConfig = getSeasonConfig(cachedStatsConfig, selectedSeason);

    const regions = (seasonConfig.vsTeams || []).map((t) => t.region).filter(Boolean);
    setAvailableRegions([...new Set(regions)].sort());
    setMatchTypes(seasonConfig.matchTypes || []);

    const players = cachedStatsConfig?.players || [];
    const imagesMap = {};
    const active = [];

    players.forEach((p) => {
      if (p.ign && p.imageUrl) {
        imagesMap[p.ign] = p.imageUrl;
      }
      if (p.isActive !== false) {
        active.push(p.ign || p.name || '');
      }
    });

    setPlayerImages(imagesMap);
    setActivePlayers(active);
  }, [cachedStatsConfig, selectedSeason]);

  useEffect(() => {
    if (selectedSeason && SEASONS[selectedSeason] && (!startDate || !endDate)) {
      const season = SEASONS[selectedSeason];
      setStartDate(season.startDate);
      setEndDate(season.endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason]);

  useEffect(() => {
    if (!teamId || !bootstrapLoaded) return;
    if (seasons.length > 0 && !seasonDocId) return;

    const fetchId = ++fetchCounterRef.current;
    const filters = { startDate, endDate, matchType, region };
    const diagnostic = createLoadDiagnostic({
      uid,
      teamId,
      selectedSeason,
      seasonDocId,
      startDate,
      endDate,
      matchType,
      region,
      mapFilters: latestMapFiltersRef.current,
      seasons,
    });
    const cacheKey = buildCacheKey({
      teamId,
      seasonDocId,
      selectedSeason,
      playerId: 'all',
      startDate,
      endDate,
      matchType,
      region,
    });

    const cachedRows = getCachedRows(cacheKey);
    if (cachedRows) {
      recordSourceAttempt(diagnostic, {
        source: 'dashboard-memory-cache',
        path: `playerEntriesSessionCache:${cacheKey}`,
        status: 'SUCCESS',
        documentCount: cachedRows.length,
        normalizedRowCount: cachedRows.length,
        filteredRowCount: cachedRows.length,
        filterBreakdown: getFilterBreakdown(cachedRows, filters),
        ...summarizeRowsForDiagnostic(cachedRows),
      });
      selectDiagnosticSource(diagnostic, 'dashboard-memory-cache');
      diagnostic.maintenanceAssignments.push('cache hit: setStatsMaintenance(false)');
      diagnostic.maintenanceMode = false;
      diagnostic.statsMaintenance = false;
      diagnostic.filterBreakdown = getFilterBreakdown(cachedRows, filters, latestMapFiltersRef.current);
      pendingDiagnosticRef.current = diagnostic;
      const cachedUniqueMatches = getUniqueMatchCount(cachedRows);
      setTotalGamesCount(cachedUniqueMatches || cachedRows.length || 0);
      setPlayerStats(cachedRows);
      setStatsMaintenance(false);
      setLoading(false);
      return;
    }

    let active = true;

    (async () => {
      setLoading(true);
      setStatsMaintenance(false);
      if (diagnostic) {
        diagnostic.maintenanceAssignments.push('load start: setStatsMaintenance(false)');
      }

      let rows = [];
      let matchCount = 0;
      const dataSourceUnavailable = false;

      if (seasonDocId) {
        const seasonCandidates = [...new Set([String(seasonDocId), String(selectedSeason || '')].filter(Boolean))];

        for (const candidateSeasonId of seasonCandidates) {
          const preferred = await fetchPreferredFlatEntries({ teamId, seasonDocId: candidateSeasonId, filters, diagnostic });
          rows = preferred.entries;
          matchCount = preferred.matchCount || 0;
          if (rows.length) {
            selectDiagnosticSource(diagnostic, preferred.source === 'flatSeasonEntriesCache' ? 'season-flat-session-cache' : 'season-flat');
          }

          if (!rows.length) {
            const legacySeasonRows = await fetchLegacySeasonEntries({ teamId, seasonDocId: candidateSeasonId, filters, diagnostic });
            rows = mergeUniqueRows(rows, legacySeasonRows);
            matchCount = getUniqueMatchCount(rows);
            if (legacySeasonRows.length) {
              selectDiagnosticSource(diagnostic, 'season-nested-matches');
            }
          }

          if (!rows.length) {
            if (diagnostic) {
              diagnostic.maintenanceAssignments.push(
                `season candidate ${candidateSeasonId}: rows.length === 0 after preferred and legacy season sources; keeping statsMaintenance false because empty filtered results are a normal empty state`
              );
            }
          }

          if (rows.length) {
            break;
          }
        }
      } else {
        const rootFlatRows = await fetchRootFlatEntries({ teamId, filters, diagnostic });
        if (rootFlatRows.length) {
          selectDiagnosticSource(diagnostic, 'root-flat');
        }
        const rootNestedRows = await fetchRootMatches({ teamId, filters, diagnostic });
        if (!rootFlatRows.length && rootNestedRows.length) {
          selectDiagnosticSource(diagnostic, 'root-nested-matches');
        }
        rows = mergeUniqueRows(rootFlatRows, rootNestedRows);
        matchCount = getUniqueMatchCount(rows);
      }

      if (!rows.length && seasonDocId) {
        const rootFlatRows = await fetchRootFlatEntries({ teamId, filters, diagnostic });
        if (rootFlatRows.length) {
          selectDiagnosticSource(diagnostic, 'root-flat');
        }
        const rootNestedRows = await fetchRootMatches({ teamId, filters, diagnostic });
        if (!rootFlatRows.length && rootNestedRows.length) {
          selectDiagnosticSource(diagnostic, 'root-nested-matches');
        }
        rows = mergeUniqueRows(rootFlatRows, rootNestedRows);
        matchCount = getUniqueMatchCount(rows);
        if (rows.length) {
          if (diagnostic) {
            diagnostic.maintenanceAssignments.push('root fallback produced rows: statsMaintenance remains false');
          }
        }
      }

      if (!rows.length) {
        rows = await fetchLegacyRootPlayerStats({ teamId, filters, diagnostic });
        matchCount = getUniqueMatchCount(rows);
        if (rows.length) {
          selectDiagnosticSource(diagnostic, 'legacy-root-teamstats-playerstats');
          if (diagnostic) {
            diagnostic.maintenanceAssignments.push('legacy root playerstats produced rows: statsMaintenance remains false');
          }
        }
      }

      if (!matchCount) {
        const uniqueMatches = getUniqueMatchCount(rows);
        matchCount = uniqueMatches || rows.length || 0;
      }

      if (!active || fetchId !== fetchCounterRef.current) return;

      setTotalGamesCount(matchCount);
      setPlayerStats(rows);
      const nextStatsMaintenance = dataSourceUnavailable;
      if (diagnostic) {
        diagnostic.maintenanceMode = dataSourceUnavailable;
        diagnostic.statsMaintenance = nextStatsMaintenance;
        diagnostic.filterBreakdown = {
          ...diagnostic.filterBreakdown,
          afterMap: getFilterBreakdown(rows, filters, latestMapFiltersRef.current).afterMap,
        };
        if (!diagnostic.selectedDataSource && rows.length) {
          recordRowsDiagnostic(diagnostic, rows, filters, 'unknown');
        }
        diagnostic.maintenanceAssignments.push(
          `final: setStatsMaintenance(${nextStatsMaintenance}) from dataSourceUnavailable (${dataSourceUnavailable}); rows.length === 0 is handled by the normal empty state`
        );
        pendingDiagnosticRef.current = diagnostic;
      }
      setStatsMaintenance(nextStatsMaintenance);
      if (rows.length) {
        setCachedRows(cacheKey, rows);
      }
    })().catch((err) => {
      console.error('Failed to load player stats:', err);
      if (!active || fetchId !== fetchCounterRef.current) return;
      if (diagnostic) {
        diagnostic.errors.push({
          source: 'main-load',
          code: err?.code || '',
          message: err?.message || String(err),
        });
        diagnostic.maintenanceAssignments.push('main load catch: setStatsMaintenance(true) because the player stats data source threw before a valid empty result could be established');
        diagnostic.maintenanceMode = true;
        diagnostic.statsMaintenance = true;
        pendingDiagnosticRef.current = diagnostic;
      }
      setTotalGamesCount(0);
      setPlayerStats([]);
      setStatsMaintenance(true);
      toast.error('Failed to load player statistics');
    }).finally(() => {
      if (!active || fetchId !== fetchCounterRef.current) return;
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [uid, teamId, seasonDocId, selectedSeason, startDate, endDate, matchType, region, bootstrapLoaded, seasons]);

  const exportToCSV = () => {
    try {
      const headers = ['Mode', 'Player', 'Date', 'Match Type', 'Region', 'Map', 'K', 'D', 'A', 'Score', 'Impact'];
      const rows = playerStats
        .filter((row) => {
          if (startDate && row.Date && row.Date < startDate) return false;
          if (endDate && row.Date && row.Date > endDate) return false;
          if (matchType && normalizeText(row.MatchType) !== normalizeText(matchType)) return false;
          if (region && normalizeText(row.Region) !== normalizeText(region)) return false;
          return true;
        })
        .map((row) => [
          row.Mode || '',
          row.Player || '',
          row.Date || '',
          row.MatchType || '',
          row.Region || '',
          row.Map || '',
          row.K || 0,
          row.D || 0,
          row.A || 0,
          row.Score || 0,
          row.Impact || 0,
        ]);

      const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `player_stats_${formatDateForInput(new Date())}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('CSV file downloaded successfully!');
    } catch (err) {
      console.error('Failed to export CSV:', err);
      toast.error('Failed to export CSV');
    }
  };

  const togglePlayerComparison = (playerName) => {
    setComparePlayers((prev) => {
      if (prev.includes(playerName)) {
        return prev.filter((p) => p !== playerName);
      }
      if (prev.length < 3) {
        return [...prev, playerName];
      }
      toast.info('You can compare up to 3 players at a time');
      return prev;
    });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    const withinDate = (row) => {
      if (startDate && row.Date && row.Date < startDate) return false;
      if (endDate && row.Date && row.Date > endDate) return false;
      return true;
    };

    const applyFilters = (mode) =>
      playerStats.filter((row) => {
        if (row.Mode !== mode) return false;
        if (matchType && normalizeText(row.MatchType) !== normalizeText(matchType)) return false;
        if (region && normalizeText(row.Region) !== normalizeText(region)) return false;
        if (mapFilters[mode] && row.Map !== mapFilters[mode]) return false;
        if (!withinDate(row)) return false;
        return true;
      });

    const nextData = { HP: [], SND: [], CTL: [] };
    const nextModeInsights = { HP: [], SND: [], CTL: [] };

    MODES.forEach((mode) => {
      const filtered = applyFilters(mode);
      const grouped = filtered.reduce((acc, row) => {
        const key = row.Player || 'Unknown';
        if (!acc[key]) {
          acc[key] = {
            Player: key,
            kills: 0,
            deaths: 0,
            assists: 0,
            score: 0,
            impact: 0,
            matches: 0,
            wins: 0,
            losses: 0,
            hillTimeTotal: 0,
            captureKillsTotal: 0,
            distanceTotal: 0,
            operatorValueTotal: 0,
            damageTotal: 0,
            roundsTotal: 0,
            adrTotal: 0,
            firstBloodsTotal: 0,
            clutches1v1Total: 0,
            ctlAdrTotal: 0,
          };
        }
        acc[key].matches += 1;
        acc[key].kills += toNumber(row.K);
        acc[key].deaths += toNumber(row.D);
        acc[key].assists += toNumber(row.A);
        acc[key].score += toNumber(row.Score);
        acc[key].impact += toNumber(row.Impact);
        acc[key].hillTimeTotal += toHillSeconds(row.HillTime);
        acc[key].captureKillsTotal += toNumber(row.CaptureKills);
        acc[key].distanceTotal += toNumber(row.DistanceMoved);
        acc[key].operatorValueTotal += toNumber(row.OperatorValue);
        acc[key].damageTotal += toNumber(row.TotalDamage || row.Damage);
        acc[key].roundsTotal += toNumber(row.PF) + toNumber(row.PA);
        acc[key].adrTotal += toNumber(row.ADR);
        acc[key].firstBloodsTotal += toNumber(row.FirstBloods);
        acc[key].clutches1v1Total += toNumber(row.Clutches1v1);
        const rowRounds = toNumber(row.PF) + toNumber(row.PA);
        if (rowRounds > 0) {
          acc[key].ctlAdrTotal += toNumber(row.TotalDamage || row.Damage) / rowRounds;
        }
        const result = normalizeResult(row.WinLose || row.Result);
        if (result === 'Win') acc[key].wins += 1;
        if (result === 'Loss') acc[key].losses += 1;
        return acc;
      }, {});

      const modePlayers = Object.values(grouped)
        .map((p) => ({
          Player: p.Player,
          Score: p.score / p.matches,
          K: p.kills / p.matches,
          D: p.deaths / p.matches,
          A: p.assists / p.matches,
          Impact: Number((p.impact / p.matches || 0).toFixed(2)),
          matches: p.matches,
          totalScore: p.score,
          totalK: p.kills,
          totalD: p.deaths,
          totalA: p.assists,
          totalImpact: p.impact,
          wins: p.wins,
          losses: p.losses,
          winRate: p.matches ? ((p.wins / p.matches) * 100) : 0,
          avgHillTime: p.matches ? (p.hillTimeTotal / p.matches) : 0,
          hillTimeTotal: p.hillTimeTotal,
          pressureScore: p.matches ? ((p.damageTotal + p.distanceTotal) / p.matches) : 0,
          pressureTotal: (p.damageTotal + p.distanceTotal),
          operatorImpact: p.matches ? (p.operatorValueTotal / p.matches) : 0,
          operatorValueTotal: p.operatorValueTotal,
          hillBreaker: p.matches ? (p.captureKillsTotal / p.matches) : 0,
          captureKillsTotal: p.captureKillsTotal,
          adr: p.matches ? (p.adrTotal / p.matches) : 0,
          adrTotal: p.adrTotal,
          entryKills: p.matches ? (p.firstBloodsTotal / p.matches) : 0,
          firstBloodsTotal: p.firstBloodsTotal,
          clutches1v1: p.matches ? (p.clutches1v1Total / p.matches) : 0,
          clutches1v1Total: p.clutches1v1Total,
          survivability: (p.kills + p.deaths) > 0 ? (p.kills / (p.kills + p.deaths)) : 0,
          objectiveScore: p.matches ? ((p.captureKillsTotal + p.impact) / p.matches) : 0,
          objectiveScoreTotal: (p.captureKillsTotal + p.impact),
          kd: p.deaths > 0 ? (p.kills / p.deaths) : p.kills,
          avgScore: p.matches ? (p.score / p.matches) : 0,
          ctlAdr: p.matches > 0 ? (p.ctlAdrTotal / p.matches) : 0,
          ctlAdrTotal: p.ctlAdrTotal,
        }));
      nextModeInsights[mode] = buildModeInsights(mode, modePlayers);
      nextData[mode] = modePlayers.slice(0, 6);

      if (mode === 'HP') {
        setHpRankings(
          modePlayers.map((p) => ({
            Player: p.Player,
            Objective: Number(p.avgHillTime || 0),
            ObjectiveTotal: Number(p.hillTimeTotal || 0),
            HillBreaker: Number(p.hillBreaker || 0),
            HillBreakerTotal: Number(p.captureKillsTotal || 0),
            PressurePlayer: Number(p.pressureScore || 0),
            PressurePlayerTotal: Number(p.pressureTotal || 0),
            OperatorImpact: Number(p.operatorImpact || 0),
            OperatorImpactTotal: Number(p.operatorValueTotal || 0),
            Survivability: Number(p.survivability || 0),
          }))
        );
      }

      if (mode === 'SND') {
        setSndRankings(
          modePlayers.map((p) => ({
            Player: p.Player,
            ADR: Number(p.adr || 0),
            ADRTotal: Number(p.adrTotal || 0),
            FirstKills: Number(p.entryKills || 0),
            FirstKillsTotal: Number(p.firstBloodsTotal || 0),
            Clutches1v1: Number(p.clutches1v1 || 0),
            Clutches1v1Total: Number(p.clutches1v1Total || 0),
            Survivability: Number(p.survivability || 0),
          }))
        );
      }

      if (mode === 'CTL') {
        setCtlRankings(
          modePlayers.map((p) => ({
            Player: p.Player,
            ADR: Number(p.ctlAdr || 0),
            ADRTotal: Number(p.ctlAdrTotal || 0),
            ObjectiveLeader: Number(p.objectiveScore || 0),
            ObjectiveLeaderTotal: Number(p.objectiveScoreTotal || 0),
            OperatorImpact: Number(p.operatorImpact || 0),
            OperatorImpactTotal: Number(p.operatorValueTotal || 0),
            SurvivabilityLeader: Number(p.survivability || 0),
          }))
        );
      }
    });

    setData(nextData);
    setModeInsights(nextModeInsights);

    const allPlayers = new Set();
    const allRankings = [];

    MODES.forEach((mode) => {
      const filtered = applyFilters(mode);
      filtered.forEach((row) => {
        if (row.Player) allPlayers.add(row.Player);
      });

      filtered.forEach((row) => {
        const kd = row.D ? Number((row.K / row.D).toFixed(2)) : Number(row.K || 0);
        allRankings.push({ Player: row.Player || 'Unknown', KD: kd });
      });
    });

    const topKD = allRankings.length > 0 ? allRankings.sort((a, b) => b.KD - a.KD)[0] : null;

    const allFilteredRows = playerStats.filter((row) => {
      if (matchType && normalizeText(row.MatchType) !== normalizeText(matchType)) return false;
      if (region && normalizeText(row.Region) !== normalizeText(region)) return false;
      if (startDate && row.Date && row.Date < startDate) return false;
      if (endDate && row.Date && row.Date > endDate) return false;
      return true;
    });
    const totalWins = allFilteredRows.filter((row) => normalizeResult(row.Result || row.WinLose) === 'Win').length;
    const totalMatchesForRate = allFilteredRows.length;
    const pfTotal = allFilteredRows.reduce((sum, row) => sum + Number(row.PF || 0), 0);
    const paTotal = allFilteredRows.reduce((sum, row) => sum + Number(row.PA || 0), 0);
    const avgScoreDiff = totalMatchesForRate ? ((pfTotal - paTotal) / totalMatchesForRate) : 0;

    setSummaryStats({
      totalPlayers: allPlayers.size,
      totalGames: totalGamesCount,
      topKD,
      teamWinRate: totalMatchesForRate ? (totalWins / totalMatchesForRate) * 100 : 0,
      avgScoreDiff,
    });

    if (PLAYER_DASHBOARD_DIAGNOSTICS && pendingDiagnosticRef.current) {
      const diagnostic = pendingDiagnosticRef.current;
      const afterMapRows = playerStats.filter((row) => {
        const mode = normalizeModeValue(row.Mode);
        return !(mode && mapFilters[mode] && row.Map !== mapFilters[mode]);
      });
      diagnostic.filterBreakdown = {
        ...diagnostic.filterBreakdown,
        afterMap: afterMapRows.length,
      };
      diagnostic.finalCounts = {
        HP: nextData.HP.length,
        SND: nextData.SND.length,
        CTL: nextData.CTL.length,
      };
      flushLoadDiagnostic(diagnostic);
      pendingDiagnosticRef.current = null;
    }
  }, [playerStats, totalGamesCount, startDate, endDate, region, matchType, mapFilters, selectedSeason, activePlayers]);

  const rankingsByMode = {
    HP: hpRankings,
    SND: sndRankings,
    CTL: ctlRankings,
  };

  const matchedSeason =
    seasons.find((s) => String(s.year || s.id) === String(selectedSeason)) ||
    seasons.find((s) => String(s.id) === String(selectedSeason)) ||
    null;
  const selectedSeasonText = String(selectedSeason || '');
  const selectedSeasonLooksLikeId = selectedSeasonText.length > 12 && !/\s/.test(selectedSeasonText) && !/^\d{4}$/.test(selectedSeasonText);
  const seasonDisplayLabel =
    matchedSeason?.label ||
    matchedSeason?.name ||
    (matchedSeason?.year ? `${matchedSeason.year} Season` : '') ||
    (selectedSeasonText
      ? (/^\d{4}$/.test(selectedSeasonText) ? `${selectedSeasonText} Season` : (selectedSeasonLooksLikeId ? 'Current Season' : selectedSeasonText))
      : 'Current Season');

  return {
    teamId,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    matchType,
    setMatchType,
    region,
    setRegion,
    matchTypes,
    availableRegions,
    mapFilters,
    setMapFilters,
    data,
    modeInsights,
    rankingsByMode,
    playerStats,
    statsMaintenance,
    loading,
    selectedPlayer,
    setSelectedPlayer,
    summaryStats,
    playerSearch,
    setPlayerSearch,
    sortConfig,
    comparePlayers,
    showComparison,
    setShowComparison,
    playerImages,
    selectedSeason,
    seasonDocId,
    seasonDisplayLabel,
    setSelectedSeason,
    activePlayers,
    exportToCSV,
    togglePlayerComparison,
    handleSort,
  };
};

export default usePlayerDashboard;
